import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import useToggle from '../../common/hooks/useToggle';
import OntraccrButton from '../../common/buttons/OnTraccrButton';
import { getIdMap } from '../../helpers/helpers';
import Permissions from '../../auth/Permissions';
import SimpleFormModal from '../../common/modals/SimpleFormModal';
import { addShortListPermission, deleteShortListPermission, updateShortListPermission } from '../state/boards.actions';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

export default function ShortListPermissionsTab({
  shortList,
}) {
  const dispatch = useDispatch();

  const shortListPermissionMap = useSelector((state) => state.boards.shortListPermissionMap);
  const users = useSelector((state) => state.users.users);

  const { toggle: togglePermission, isToggled: isToggledPermission } = useToggle();
  const [selectedUsers, setSelectedUsers] = useState([]);

  const onTogglePermission = useCallback(() => {
    togglePermission();
    setSelectedUsers([]);
  }, [togglePermission]);

  const relevantPermissions = useMemo(() => (
    shortListPermissionMap?.[shortList.id] || []
  ), [shortListPermissionMap, shortList]);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const userOptions = useMemo(() => {
    const existingUsers = new Set(relevantPermissions.map(({ userId }) => userId));
    return users
      .filter((user) => !existingUsers.has(user.id) && user.active && user.id !== Permissions.id)
      .map((user) => ({
        value: user.id,
        label: user.name,
      }));
  }, [relevantPermissions, users]);

  const onAddPermission = useCallback(async () => {
    if (!selectedUsers.length) return;
    const res = await dispatch(addShortListPermission(shortList.id, { users: selectedUsers }));
    if (res) onTogglePermission();
  }, [shortList, selectedUsers, onTogglePermission]);

  const onUpdatePermission = ({
    record,
    changedProperty,
  }) => (newValue) => {
    const payload = {
      canView: !!record.canView,
      canEdit: !!record.canEdit,
      [changedProperty]: newValue,
    };

    // In order to edit, the user must have view permissions
    if (changedProperty === 'canEdit' && newValue) {
      payload.canView = true;
    }

    // If the user does not have view permissions, they cannot edit
    if (changedProperty === 'canView' && !newValue) {
      payload.canEdit = false;
    }

    dispatch(updateShortListPermission(record.boardShortListId, record.userId, payload));
  };

  const onDeletePermission = useCallback((record) => {
    CustomConfirmModal({
      title: 'Delete Permission',
      content: 'Are you sure you want to delete this permission?',
      onOk: () => dispatch(deleteShortListPermission(shortList.id, record.userId)),
      zIndex: 5000,
    });
  }, [shortList]);

  const permissionColumns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'userId',
      render: (userId) => userMap[userId]?.name || 'Unknown User',
    },
    {
      title: 'View',
      dataIndex: 'canView',
      render: (canView, record) => (
        <OnTraccrCheckbox
          value={canView}
          onChange={onUpdatePermission({ record, changedProperty: 'canView' })}
        />
      ),
    },
    {
      title: 'Edit',
      dataIndex: 'canEdit',
      render: (canEdit, record) => (
        <OnTraccrCheckbox
          value={canEdit}
          onChange={onUpdatePermission({ record, changedProperty: 'canEdit' })}
        />
      ),
    },
    {
      title: '',
      dataIndex: 'delete',
      width: 30,
      render: (_, record) => (
        <BorderlessButton
          iconNode={<DeleteOutlined style={{ color: 'red' }} />}
          onClick={() => onDeletePermission(record)}
        />
      ),
    },
  ], [onDeletePermission, userMap]);

  return (
    <>
      <OntraccrButton
        title="Add Permission"
        onClick={onTogglePermission}
      />
      <Table
        dataSource={relevantPermissions}
        columns={permissionColumns}
        size="small"
        key="userId"
        pagination={false}
      />
      <SimpleFormModal
        title="Add Permission"
        visible={isToggledPermission}
        onClose={onTogglePermission}
        onSave={onAddPermission}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a User"
          value={selectedUsers}
          onChange={setSelectedUsers}
          mode="multiple"
          options={userOptions}
          optionFilterProp="label"
          showSearch
        />
      </SimpleFormModal>
    </>
  );
}

ShortListPermissionsTab.propTypes = {
  shortList: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};
